/*!
 * Define limiter properties and behaviour
 */
export default class Limiter {
    /* DEFAULT LOGIC (METHODS) THAT SHOULD BE IMPLEMENTED IN EACH PLUGIN */

    //Used for getting current values for save
    getValues() {
        return {
            thresholdValue: this.thresholdValue,
            outCeilingValue: this.outCeilingValue,
            releaseValue: this.releaseValue
        }
    }

    //Used for setting stored values
    setValues(values) {        
        this.updateThreshold(values.thresholdValue);
        this.updateOutCeiling(values.outCeilingValue);
        this.updateRelease(values.releaseValue);
    }
    
    //Rows: array of rows (visually each row will be stacked vertically), inside array of elements - they will be stacked horizontally in a row - affectors (value changers) or indicators
    //Availale kinds: 'indicator', 'affector'
    //Available types for affectors: 'round-button' (min, max, step), 'slider' (min, max, step),
    //Available types for indicators: 'vu-meter' (min, max, orientation(vertical, horizontal))
    //Indicator can show values in pure value (like db) or percent, but better in pure value. Examples: { value: -15 }, { percent: 50 }
    getUI() {
        return {
            rows: [
                [
                    {
                        kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'In', min: -60, max: 3, getValue: () => {
                            return {
                                value: this.preMetering
                            }
                        }
                    },
                    {
                        kind: 'affector', type: 'round-button', width: 120, color: '#2eda2e', title: 'Threshold', min: -30, max: 0, step: 0.1, round: 1, unit: 'dB', setValue: (v) => this.updateThreshold(v), getValue: () => this.thresholdValue
                    },
                    {
                        kind: 'affector', type: 'round-button', width: 80, orientation: 'vertical', color: '#2edad5', title: 'Out ceiling', min: -30, max: 0, step: .1, round: 1, unit: 'dB', setValue: (v) => this.updateOutCeiling(v), getValue: () => this.outCeilingValue
                    },
                    {
                        kind: 'affector', type: 'round-button', width: 80, color: '#a770f9', title: 'Release', min: 1, max: 1000, step: 1, unit: 'ms', setValue: (v) => this.updateRelease(v), getValue: () => this.releaseValue
                    },
                    {
                        kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Out', min: -60, max: 3, getValue: () => {
                            return {
                                value: this.postMetering
                            }
                        }
                    },
                    {
                        kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Attenuation', min: 0, max: 20, redPoint: 18, yellowPoint: 15, getValue: () => {
                            return {
                                value: this.attenuationMetering
                            }
                        }
                    },
                ]
            ],
            width: 640,
            height: 320,
            color: '#900'
        }
    }
    
    //in opts could be (isDebug, audioContext)
    getAudioPipeline(opts) {
        if (this.isDebug) {
            console.debug('Get audio pipeline', { opts });
        }
        //Use logic to create nodes only in getAudioPipeline
        this.ctx = opts?.audioContext || new AudioContext(); // The Audio Context we're working with 
        this.dcNode = this.ctx.createDynamicsCompressor(); // The DynamicsCompressorNode
        this.makeupGainNode = this.ctx.createGain(); // The gain node to add auto makeup gain
        this.postDCGainNode = this.ctx.createGain(); // The final output gain node
        
        // Analyser properties
        this.fttSize = 2048; // bytes to allocate for data
        this.preAnalyser = this.ctx.createAnalyser(); // The input audio analyser
        this.postAnalyser = this.ctx.createAnalyser(); // The output audio analyser
        this.preFloatTimeData; // Generated by preAnalyser
        this.postFloatTimeData; // Generated by postAnalyser

        this.playing = false; // Whether or not an audio file is currently playing through this limiter

        this.preMetering = null;
        this.postMetering = null;
        this.attenuationMetering = null;

        this.preAnalyser.connect(this.dcNode);
        this.dcNode.connect(this.makeupGainNode);
        this.makeupGainNode.connect(this.postDCGainNode);
        this.postDCGainNode.connect(this.postAnalyser);        

        this.initPreAnalyser();
        this.initNodeValues();
        this.initPostAnalyser();

        //To input will be connected source (with other effects), output will be connected to destination (or next effect)
        return {
            input: this.preAnalyser,
            output: this.postAnalyser
        }
    }

    //Called on playing state change (1 - playing, 0 - stop)
    setPlayState(value) {
        if (this.isDebug) {
            console.debug('Set play state changed', { value });
        }
        this.playing = !!value;
        
        if (this.playing)
            this.updateMeters();
    }

    //in opts could be (isDebug, audioContext)
    constructor(opts) {
        this.isDebug = opts?.isDebug;
        this.opts = opts;        

        //Show something in console only in debug mode
        if (this.isDebug) {
            console.log('Creating limiter with', { opts });            
        }

        //Do not make here any UI logic

        // Default limiter settings
        this.threshold = 0;
        this.knee = 30;
        this.ratio = 20;
        this.attack = .01;
        this.release = .1;
        this.outCeiling = 1;

        this.makeupGain = 1;
        this.postDCGain = 1;

        this.thresholdValue = 0;
        this.outCeilingValue = 0;
        this.releaseValue = 100;
    }

    /* PLUGIN LOGIC */

    initNodeValues() {
        // Initialize DynamicsCompressNode values
        this.dcNode.threshold.value = this.threshold;
        this.dcNode.knee.value = this.knee;
        this.dcNode.ratio.value = this.ratio; // 20 is max
        this.dcNode.attack.value = this.attack;
        this.dcNode.release.value = this.release;

        // Initialize makeup GainNode value
        this.makeupGainNode.gain.setValueAtTime(this.makeupGain, this.ctx.currentTime);

        // Initialize post DC GainNode value
        this.postDCGainNode.gain.setValueAtTime(this.postDCGain, this.ctx.currentTime);
    }

    initPreAnalyser() {
        // Analyzer for metering
        this.preAnalyser.fftSize = this.fttSize;
        
        this.preFloatTimeData = new Float32Array(this.preAnalyser.frequencyBinCount);
        this.preAnalyser.getFloatTimeDomainData(this.preFloatTimeData);
    }

    initPostAnalyser() {
        // Analyzer for metering
        this.postAnalyser.fftSize = this.fttSize;
        
        this.postFloatTimeData = new Float32Array(this.postAnalyser.frequencyBinCount);
        this.postAnalyser.getFloatTimeDomainData(this.postFloatTimeData);
    }

    updateThreshold(value) {
        this.thresholdValue = value;
        this.threshold = value;
        console.log('updating threshold to: ' + this.threshold);

        if (typeof this.dcNode != 'undefined') {
            this.dcNode.threshold.setValueAtTime(this.threshold, this.ctx.currentTime);
        }
        
        this.updateMakeupGain();
    }

    // Update auto makeup gain
    updateMakeupGain() {
        //  Never below 1, we never want to make it quieter with the makup gain
        this.makeupGain = this.convertDecibelsToGain(Math.abs(this.threshold/6)); 
        
        if (this.isDebug)
            console.log('setting make up gain to ' +  this.makeupGain, { threshold: this.threshold });
        if (typeof this.makeupGainNode != 'undefined') {
            this.makeupGainNode.gain.setValueAtTime(this.makeupGain, this.ctx.currentTime);
        }
    }

    updateOutCeiling(value) {
        this.outCeilingValue = value;
        this.outCeiling = this.convertDecibelsToGain(value);
        
        if (this.isDebug)
            console.log('updating updateOutCeiling to: ' + this.outCeiling);

        if (typeof this.postDCGainNode != 'undefined') {
            this.postDCGainNode.gain.setValueAtTime(this.outCeiling, this.ctx.currentTime);
        }
    }

    updateRelease(value) {
        this.releaseValue = value;
        this.release = value / 1000; // Max release is 1 or 1000ms
        if (this.isDebug)
            console.log('updating eRelease to: ' + this.release);

        if (typeof this.dcNode != 'undefined') {            
            this.dcNode.release.setValueAtTime(this.release, this.ctx.currentTime);
        }        
    }
  

    // The meters are acting as peak meters
    updateMeters() {
        // Schedule the next update
        if (this.playing) {
            requestAnimationFrame(() => this.updateMeters()); // Recursive
        }

        this.updatePreMeters();
        this.updatePostMeters();
        this.updateAttenuationMeter();

        // console.log('pre gain val: ' + this.makeupGainNode.gain.value);
        // console.log('post gain val: ' + this.postDCGainNode.gain.value);
    }

    updatePreMeters() {
        // Get the new frequency data
        this.preAnalyser.getFloatTimeDomainData(this.preFloatTimeData);
        // console.log(this.preFloatTimeData);

        let sampleBuffer = this.preFloatTimeData;
        // Compute peak instantaneous power over the interval.
        let peakInstantaneousPower = 0;
        for (let i = 0; i < sampleBuffer.length; i++) {
            const power = sampleBuffer[i] ** 2;
            peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
        }
        const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

        // console.log('mpeakInstantaneousPowerDecibels: ' + peakInstantaneousPowerDecibels);
        this.preMetering = peakInstantaneousPowerDecibels;
    }

    updatePostMeters() {
       // Get the new frequency data
       this.postAnalyser.getFloatTimeDomainData(this.postFloatTimeData);
       // console.log(this.postFloatTimeData);
       let sampleBuffer = this.postFloatTimeData;
       // Compute peak instantaneous power over the interval.
       let peakInstantaneousPower = 0;
       for (let i = 0; i < sampleBuffer.length; i++) {
           const power = sampleBuffer[i] ** 2;
           peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
       }
       const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

       // console.log('mpeakInstantaneousPowerDecibels: ' + peakInstantaneousPowerDecibels);
       this.postMetering = peakInstantaneousPowerDecibels;
    }

    updateAttenuationMeter() {
        // Get the reduction value in dB from the compressor node
		let totalReduction = 0;

		if (this.postMetering < this.preMetering) {
			totalReduction = Math.abs(this.preMetering - this.postMetering);
		}

		this.attenuationMetering = totalReduction;
    }

    convertDecibelsToGain(db) {
        return Math.pow(10, (db / 20));
    }

    convertGainToDecibels(gain) {
        return 20 * Math.log10( gain );
    }
}